(() => {
  if (customElements.get('rebuy-featured-collection')) return;

  // Share requests between instances, and avoid sending 25 requests at once.
  const cardRequests = new Map();
  const requestQueue = [];
  let activeRequests = 0;

  function runRequests() {
    while (activeRequests < 4 && requestQueue.length) {
      const { url, resolve, reject } = requestQueue.shift();
      activeRequests += 1;
      fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) })
        .then((response) => {
          if (!response.ok) throw new Error(`Product card request failed (${response.status})`);
          return response.text();
        })
        .then(resolve, reject)
        .finally(() => {
          activeRequests -= 1;
          runRequests();
        });
    }
  }

  function fetchCard(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('section_id', 'rebuy-product-card');
    const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    const key = url.toString();

    if (!cardRequests.has(key)) {
      const request = new Promise((resolve, reject) => {
        requestQueue.push({ url: key, resolve, reject });
        runRequests();
      }).catch((error) => {
        cardRequests.delete(key);
        throw error;
      });
      cardRequests.set(key, request);
    }

    return cardRequests.get(key);
  }

  class RebuyProductCard extends HTMLElement {
    static get observedAttributes() {
      return ['data-handle', 'data-product-id'];
    }

    connectedCallback() {
      // Rebuy inserts the uncompiled template before Vue binds product data.
      queueMicrotask(() => this.tryLoad());
    }

    attributeChangedCallback() {
      queueMicrotask(() => this.tryLoad());
    }

    tryLoad() {
      if (!this.isConnected || this.dataset.state || !this.dataset.handle || !this.dataset.productId) return;
      this.dataset.state = 'loading';
      this.loadCard();
    }

    async loadCard() {
      const owner = this.closest('rebuy-featured-collection');
      if (!owner) {
        this.dataset.state = 'error';
        this.dispatchEvent(new CustomEvent('rebuy:card:loaded', { bubbles: true }));
        return;
      }

      try {
        const html = await fetchCard(this.dataset.handle);
        if (!this.isConnected) {
          delete this.dataset.state;
          return;
        }
        const response = new DOMParser().parseFromString(html, 'text/html');
        const card = response.querySelector('template[data-rebuy-product-card]');
        if (!card || card.dataset.productId !== this.dataset.productId) {
          throw new Error('The product card response did not contain the requested product');
        }

        this.dataset.available = card.dataset.available;
        const template = document.createElement('template');
        template.innerHTML = card.innerHTML.replaceAll('rebuy-card-placeholder', owner.dataset.sectionId);
        const link = template.content.querySelector('a');
        if (this.dataset.productUrl) link.setAttribute('href', this.dataset.productUrl);

        if (owner.dataset.showSecondaryImage !== 'true') {
          template.content.querySelector('.second-image')?.remove();
          template.content.querySelector('img')?.classList.remove('group-hover/image:opacity-0');
        }

        if (owner.dataset.quickAdd !== 'standard') {
          template.content.querySelector('.quick-add')?.remove();
          link.classList.remove('border-b-0');
        }

        if (owner.dataset.sectionIndex === '1' && Number(this.dataset.productIndex) < 4) {
          const image = template.content.querySelector('img');
          image?.setAttribute('loading', 'eager');
          image?.setAttribute('fetchpriority', 'high');
        }

        // The existing product-form handles the cart drawer and store cart limits.
        // Supply quantity explicitly because its limit checks read this form value.
        const form = template.content.querySelector('form');
        if (form) {
          const quantity = document.createElement('input');
          quantity.type = 'hidden';
          quantity.name = 'quantity';
          quantity.value = '1';
          form.append(quantity);
        }

        // New buttons arrive after the Klaviyo form's initial event binding.
        template.content.querySelectorAll('[popovertarget="back-in-stock"]').forEach((button) => {
          button.addEventListener('click', () => {
            sessionStorage.setItem('back-in-stock', button.dataset.detail);
          });
        });

        this.replaceChildren(template.content);
        this.dataset.state = 'ready';
      } catch (error) {
        this.dataset.state = 'error';
        console.warn('[Rebuy featured collection]', error);
      }

      this.dispatchEvent(new CustomEvent('rebuy:card:loaded', { bubbles: true }));
    }
  }

  class RebuyFeaturedCollection extends HTMLElement {
    connectedCallback() {
      // Reordering a section can disconnect and reconnect the same element.
      if (this.active) return;
      const instances = [...document.querySelectorAll('rebuy-featured-collection')];
      const first = instances.find((element) => element.dataset.widgetId === this.dataset.widgetId);
      if (first !== this) {
        this.setStatus('This Rebuy widget ID is already used on this page. Choose a different widget ID for this section.');
        return;
      }
      this.active = true;
      window.VisantoRebuyContext?.register(this);
      // Rebuy moves widget-specific template scripts to document.body on init.
      this.template = this.querySelector('script[type="text/template"]')
        || document.querySelector(`script#rebuy-widget-${this.dataset.widgetId}`);

      this.onRebuyEvent = (event) => {
        const widget = event.detail?.widget;
        if (String(widget?.id) !== this.dataset.widgetId) return;
        this.widget = widget;
        this.queueUpdate();
      };
      this.onLoaded = () => window.Rebuy?.init?.();
      this.onCardLoaded = () => this.queueUpdate();
      this.onSubmit = (event) => this.addAttribution(event);
      this.events = ['rebuy.ready', 'rebuy.productsChange', 'rebuy.refresh'];
      this.events.forEach((name) => document.addEventListener(name, this.onRebuyEvent));
      document.addEventListener('rebuy.loaded', this.onLoaded);
      this.addEventListener('rebuy:card:loaded', this.onCardLoaded);
      this.addEventListener('submit', this.onSubmit, true);
      this.observer = new MutationObserver(() => this.queueUpdate());
      this.observer.observe(this, { childList: true, subtree: true });

      // Defer until all widget-specific templates and mount points are in the DOM.
      this.initTimer = setTimeout(() => {
        if (!this.isConnected) return;
        this.loadRebuy();
        this.queueUpdate();
      }, 0);
      this.statusTimer = setTimeout(() => {
        this.reportLoadingStatus();
      }, 20000);
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) this.cleanup();
      });
    }

    cleanup() {
      if (!this.active) return;
      this.active = false;
      window.VisantoRebuyContext?.unregister(this);
      clearTimeout(this.initTimer);
      clearTimeout(this.statusTimer);
      cancelAnimationFrame(this.updateFrame);
      this.observer?.disconnect();
      this.events?.forEach((name) => document.removeEventListener(name, this.onRebuyEvent));
      document.removeEventListener('rebuy.loaded', this.onLoaded);
      this.removeEventListener('rebuy:card:loaded', this.onCardLoaded);
      this.removeEventListener('submit', this.onSubmit, true);
      this.querySelector('swiper-container')?.swiper?.destroy();
      this.template?.remove();
      // Dispose only this instance. Widget.destroy() removes every placement
      // with this ID, including a replacement just inserted by the theme editor.
      const widget = this.widget || window.Rebuy?.widgets?.find((item) => String(item.id) === this.dataset.widgetId);
      if (widget) {
        widget.View?.vue?.$destroy();
        widget.unbindEvents?.();
        window.Rebuy.widgets = window.Rebuy.widgets.filter((item) => item !== widget);
      }
    }

    loadRebuy() {
      if (window.Rebuy?.init) {
        window.Rebuy.init();
        return;
      }

      if (this.dataset.loadRebuyScript !== 'true') return;
      if (document.querySelector('script[src*="cdn.rebuyengine.com/onsite/js/rebuy.js"]')) return;
      const script = document.createElement('script');
      const url = new URL('https://cdn.rebuyengine.com/onsite/js/rebuy.js');
      url.searchParams.set('assets', 'javascript');
      url.searchParams.set('shop', this.dataset.shop);
      script.src = url.toString();
      script.async = true;
      script.addEventListener('error', () => this.setStatus('Rebuy JavaScript could not be loaded.'));
      document.head.append(script);
    }

    queueUpdate() {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.update();
      });
    }

    reportLoadingStatus() {
      if (!this.querySelector('[data-rebuy-content]')?.hidden) return;
      if (!window.Rebuy?.init) {
        this.setStatus('Rebuy JavaScript has not initialized. Enable the Rebuy app embed, or enable “Load Rebuy JavaScript” in this section.');
        return;
      }

      const widget = this.widget || window.Rebuy.widgets?.find((item) => String(item.id) === this.dataset.widgetId);
      if (!widget) {
        this.setStatus(`Rebuy JavaScript is loaded, but widget ${this.dataset.widgetId} has not initialized. Check the widget ID and that the widget is enabled.`);
        return;
      }

      const products = widget.data?.products;
      const endpoint = widget.data?.config?.endpoint;
      if (!Array.isArray(products)) {
        this.setStatus(`Rebuy widget ${this.dataset.widgetId} loaded, but its recommendation data source${endpoint ? ` (${endpoint})` : ''} has not returned products. In the widget’s Recommended Products settings, select and save a working data source. Check the browser Network panel for the Rebuy API response.`);
      } else if (!this.querySelector('swiper-container') && products.length) {
        this.setStatus(`Rebuy widget ${this.dataset.widgetId} returned ${products.length} products, but its custom template has not rendered. Ensure the template snippet is included in this theme and check the browser console.`);
      } else {
        // Preserve more specific card-loading and availability diagnostics.
        this.widget = widget;
        this.update();
      }
    }

    update() {
      const content = this.querySelector('[data-rebuy-content]');
      const swiper = content?.querySelector('swiper-container');
      const slides = [...(swiper?.querySelectorAll('swiper-slide') || [])];
      if (!swiper || !slides.length) {
        if (content) content.hidden = true;
        if (this.widget) this.setStatus('This Rebuy widget is currently returning no recommendations.');
        return;
      }

      // Wait for this batch to settle so cards appear in Rebuy's order together.
      if (slides.some((slide) => slide.querySelector('rebuy-product-card')?.dataset.state === 'loading')) return;

      let visibleCount = 0;
      let errorCount = 0;
      const limit = Number(this.dataset.productsToShow);
      slides.forEach((slide) => {
        const card = slide.querySelector('rebuy-product-card');
        if (card?.dataset.state === 'error') errorCount += 1;
        const visible = card?.dataset.state === 'ready'
          && (this.dataset.showOnlyAvailable !== 'true' || card.dataset.available === 'true')
          && visibleCount < limit;
        slide.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      content.hidden = visibleCount === 0;
      this.setStatus(visibleCount ? '' : errorCount
        ? 'The Shopify product cards could not be loaded. Ensure the rebuy-product-card section is included in this theme.'
        : 'No available recommendations match the section settings.');
      if (!visibleCount) return;

      if (swiper.swiper && !swiper.swiper.destroyed) {
        swiper.swiper.update();
      } else if (typeof swiper.initialize === 'function') {
        swiper.initialize();
      } else if (!this.waitingForSwiper) {
        this.waitingForSwiper = true;
        customElements.whenDefined('swiper-container').then(() => {
          this.waitingForSwiper = false;
          if (this.isConnected) this.queueUpdate();
        });
      }
    }

    addAttribution(event) {
      const form = event.target;
      if (!form.closest('rebuy-product-card') || !form.closest('product-form')) return;
      const properties = {
        _attribution: 'Rebuy Product',
        _source: 'Rebuy',
        _widget_id: this.dataset.widgetId,
      };
      Object.entries(properties).forEach(([key, value]) => {
        const name = `properties[${key}]`;
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.append(input);
        }
        input.value = value;
      });
    }

    setStatus(message) {
      const status = this.querySelector('[data-rebuy-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }
  }

  customElements.define('rebuy-product-card', RebuyProductCard);
  customElements.define('rebuy-featured-collection', RebuyFeaturedCollection);
})();
